import { z } from 'zod';

// Environment variable schemas, one per app. Apps validate their own env at boot
// and fail fast if anything is missing or malformed.

export const NodeEnvSchema = z.enum(['development', 'test', 'production']).default('development');
export const LogLevelSchema = z
  .enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace'])
  .default('info');

export const BotEnvSchema = z.object({
  NODE_ENV: NodeEnvSchema,
  LOG_LEVEL: LogLevelSchema,
  DISCORD_BOT_TOKEN: z.string().min(1, 'DISCORD_BOT_TOKEN is required'),
  SUPABASE_URL: z.string().url(),
  SUPABASE_SERVICE_ROLE_KEY: z.string().min(1),
  DATABASE_URL: z.string().url(),
  ANTHROPIC_API_KEY: z.string().min(1),
  JINA_API_KEY: z.string().min(1, 'JINA_API_KEY is required'),
  // Cap on concurrent Jina embedding requests. Jina rejects requests past its
  // per-key concurrency limit (429); the bot shares one embeddings client
  // across the snapshot + per-record workers, so this bounds them together.
  // Defaults to 2 (the observed dev-tier limit); raise it on a higher tier.
  JINA_MAX_CONCURRENCY: z.coerce.number().int().positive().default(2),
  // Comma-separated guild ids for dev-time per-guild slash command
  // registration. Per-guild registration is instant; global registration
  // can take up to an hour. Used in NODE_ENV=development only; ignored in
  // staging/production (which always register globally). Optional - empty
  // means "no dev guilds, fall through to global registration".
  BOT_DEV_GUILD_IDS: z
    .string()
    .optional()
    .transform((raw) =>
      (raw ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    )
    .pipe(z.array(z.string().regex(/^\d{17,20}$/, 'guild id must be a Discord snowflake'))),
  // Port for the dev-only chatter ingestion endpoint (the chatter simulator
  // POSTs fake Discord messages here). Only bound when NODE_ENV=development;
  // never started in staging/production. Defaults to 8787.
  BOT_DEV_CHATTER_PORT: z.coerce.number().int().positive().default(8787),
});
export type BotEnv = z.infer<typeof BotEnvSchema>;

export const PortalEnvSchema = z.object({
  VITE_SUPABASE_URL: z.string().url(),
  VITE_SUPABASE_ANON_KEY: z.string().min(1),
  // When set, used as the base URL for OAuth redirectTo and password-reset
  // redirectTo. Falls back to window.location.origin when absent. Treat an
  // empty string the same as unset, since Vite passes through "" rather than
  // undefined for blank .env entries.
  VITE_SITE_URL: z
    .string()
    .optional()
    .transform((v) => (v === undefined || v === '' ? undefined : v))
    .pipe(z.string().url().optional()),
  // Discord application client_id used to compose the bot-install OAuth URL.
  // This is the SAME application that powers Supabase Auth Discord sign-in
  // (see SUPABASE_AUTH_EXTERNAL_DISCORD_CLIENT_ID at the repo root). Reusing
  // it is safe because OAuth consent screens are scope-driven, not
  // app-driven: the sign-in flow asks for identify/email and the bot install
  // flow asks for bot/applications.commands. The two screens never overlap.
  VITE_DISCORD_CLIENT_ID: z
    .string()
    .optional()
    .transform((v) => (v === undefined || v === '' ? undefined : v))
    .pipe(z.string().min(1).optional()),
});
export type PortalEnv = z.infer<typeof PortalEnvSchema>;

export const EdgeEnvSchema = z.object({
  SUPABASE_URL: z.string().url(),
  SUPABASE_SERVICE_ROLE_KEY: z.string().min(1),
  ANTHROPIC_API_KEY: z.string().min(1).optional(),
  STRIPE_SECRET_KEY: z.string().min(1).optional(),
  STRIPE_WEBHOOK_SECRET: z.string().min(1).optional(),
  // Discord bot-install OAuth credentials. The CLIENT_ID matches the value
  // surfaced to the portal as VITE_DISCORD_CLIENT_ID; the SECRET is server
  // side only and used to exchange the OAuth code in the discord-oauth edge
  // function. REDIRECT_URI is the portal route Discord redirects back to
  // (default http://127.0.0.1:5173/oauth/discord-callback in dev).
  DISCORD_CLIENT_ID: z.string().min(1).optional(),
  DISCORD_CLIENT_SECRET: z.string().min(1).optional(),
  DISCORD_BOT_INSTALL_REDIRECT_URI: z.string().url().optional(),
});
export type EdgeEnv = z.infer<typeof EdgeEnvSchema>;
